<template>
    <DefaultField :field="field" :errors="errors" :show-help-text="showHelpText">
        <template #field>
            <input
                ref="fileInput"
                class="visually-hidden"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                tabindex="-1"
                :disabled="!canAddMoreImages || isProcessing"
                @change="handleFileSelected"
            >

            <div class="space-y-4">
                <div
                    v-if="canAddMoreImages"
                    role="button"
                    tabindex="0"
                    class="block cursor-pointer rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/70 p-4 transition hover:border-zinc-400 focus:outline-none focus:!border-primary-500 dark:border-zinc-700 dark:bg-zinc-900/50 dark:hover:border-zinc-600 md:p-6"
                    :class="{
                        'border-primary-500 dark:border-primary-500': isDragging,
                        'pointer-events-none opacity-60': isProcessing,
                    }"
                    @click="openFilePicker"
                    @keydown.enter.prevent="openFilePicker"
                    @keydown.space.prevent="openFilePicker"
                    @dragenter.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @dragover.prevent
                    @drop.prevent="handleDrop"
                >
                    <div
                        class="pointer-events-none flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:gap-4 md:text-start"
                    >
                        <div
                            class="panel-primary-btn inline-flex !h-9 !min-h-0 w-full items-center justify-center !px-4 !text-xs md:w-auto"
                        >
                            {{ __('Add image') }}
                        </div>

                        <p class="text-sm font-medium leading-snug text-zinc-500 dark:text-zinc-400">
                            {{
                                __('Drop file or click to choose (:count/:max)', {
                                    count: images.length,
                                    max: field.maxImages,
                                })
                            }}
                        </p>
                    </div>

                    <p
                        class="pointer-events-none mt-3 text-center text-xs text-zinc-500 dark:text-zinc-400 md:text-start"
                    >
                        {{ __('Crop up to :count images. The starred image is used as the cover.', { count: field.maxImages }) }}
                    </p>
                </div>

                <p
                    v-else
                    class="text-xs text-zinc-500 dark:text-zinc-400"
                >
                    {{ __('Maximum number of images reached.') }}
                </p>

                <div v-if="images.length" class="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div
                        v-for="(image, index) in images"
                        :key="image.id"
                        class="relative overflow-hidden rounded-xl border border-zinc-200/80 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                    >
                        <img
                            :src="image.previewUrl"
                            :alt="__('Product image')"
                            class="aspect-[4/3] w-full object-cover"
                        >

                        <div class="absolute inset-x-0 top-0 flex items-center justify-between gap-2 p-2">
                            <button
                                type="button"
                                class="rounded-full px-2 py-1 text-xs font-bold"
                                :class="coverIndex === index ? 'bg-yellow-400 text-yellow-950' : 'bg-black/50 text-white'"
                                :title="__('Set as cover image')"
                                :disabled="isProcessing"
                                @click.prevent.stop="setCover(index)"
                            >
                                {{ coverIndex === index ? '★' : '☆' }}
                            </button>

                            <div class="flex gap-1">
                                <button
                                    type="button"
                                    class="rounded bg-black/50 px-2 py-1 text-xs text-white disabled:opacity-60"
                                    :disabled="isProcessing"
                                    @click.prevent.stop="openCropForExisting(image)"
                                >
                                    {{ __('Crop') }}
                                </button>
                                <button
                                    type="button"
                                    class="rounded bg-danger px-2 py-1 text-xs text-white disabled:opacity-60"
                                    :disabled="isProcessing"
                                    @click.prevent.stop="removeImage(index)"
                                >
                                    {{ __('Remove') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <p v-if="cropError" class="text-sm text-danger">
                    {{ cropError }}
                </p>
            </div>

            <CropImageModal
                ref="cropModal"
                :open="isCropDialogOpen"
                :crop-source="cropSource"
                :is-processing="isProcessing"
                :confirm-label="cropConfirmLabel"
                @close="closeCropDialog"
                @confirm="confirmCrop"
            />
        </template>
    </DefaultField>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { encode } from 'blurhash'
import { DependentFormField, HandlesValidationErrors } from '@/mixins'
import CropImageModal from '@fields/CroppedImage/js/components/CropImageModal.vue'
import { resolveStorageUrl, revokeObjectUrl } from '@fields/CroppedImage/js/utils/resolveStorageUrl'

export default {
    components: {
        CropImageModal,
    },

    mixins: [DependentFormField, HandlesValidationErrors],

    props: ['resourceName', 'resourceId', 'field'],

    data() {
        return {
            images: [],
            coverIndex: 0,
            cropSource: null,
            editingImageId: null,
            isCropDialogOpen: false,
            isProcessing: false,
            isDragging: false,
            cropError: null,
            cropperInstance: null,
        }
    },

    computed: {
        /**
         * Whether more images can be added to the gallery.
         *
         * @returns {boolean}
         */
        canAddMoreImages() {
            return this.images.length < this.field.maxImages
        },

        /**
         * Primary action label for the crop modal.
         *
         * @returns {string}
         */
        cropConfirmLabel() {
            if (this.isProcessing) {
                return this.__('Processing...')
            }

            return this.editingImageId ? this.__('Save cropped image') : this.__('Add cropped image')
        },
    },

    watch: {
        /**
         * Initializes Cropper.js after the modal transition renders the image element.
         *
         * @param {boolean} value
         */
        isCropDialogOpen(value) {
            if (value && this.cropSource) {
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.initializeCropper()
                    })
                })

                return
            }

            if (!value) {
                this.destroyCropper()
            }
        },
    },

    beforeUnmount() {
        this.images.forEach((image) => revokeObjectUrl(image.previewUrl))
        this.destroyCropper()
    },

    methods: {
        /**
         * Set the initial, internal value for the field.
         */
        setInitialValue() {
            this.images = (this.field.initialImages || []).map((image, index) => {
                const storedUrl = resolveStorageUrl(image.url, this.field.diskUrl || '')

                return {
                    id: `existing-${index}-${Date.now()}`,
                    previewUrl: storedUrl,
                    storedUrl,
                    pendingFile: null,
                    blurhash: image.blurhash || null,
                }
            })
            this.coverIndex = 0
        },

        /**
         * Opens the native file picker.
         */
        openFilePicker() {
            if (!this.canAddMoreImages || this.isProcessing) {
                return
            }

            this.$refs.fileInput?.click()
        },

        /**
         * Creates a unique id for an image item.
         *
         * @returns {string}
         */
        createImageId() {
            return `image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        },

        /**
         * Loads the selected file into the crop dialog.
         *
         * @param {Event} event
         */
        handleFileSelected(event) {
            const file = event.target.files?.[0]
            event.target.value = ''
            this.processFile(file)
        },

        /**
         * Handles a dropped image file.
         *
         * @param {DragEvent} event
         */
        handleDrop(event) {
            this.isDragging = false

            if (!this.canAddMoreImages || this.isProcessing) {
                return
            }

            const file = event.dataTransfer?.files?.[0]
            this.processFile(file)
        },

        /**
         * Reads an image file and opens the crop dialog.
         *
         * @param {File|null|undefined} file
         */
        processFile(file) {
            if (!file || !file.type.startsWith('image/')) {
                return
            }

            const reader = new FileReader()

            reader.onload = () => {
                this.cropError = null
                this.editingImageId = null
                this.cropSource = reader.result
                this.isCropDialogOpen = true
            }

            reader.readAsDataURL(file)
        },

        /**
         * Opens the crop dialog for an existing image.
         *
         * @param {{id: string, previewUrl: string}} image
         */
        openCropForExisting(image) {
            if (this.isProcessing) {
                return
            }

            this.cropError = null
            this.editingImageId = image.id
            this.cropSource = image.previewUrl
            this.isCropDialogOpen = true
        },

        /**
         * Initializes the Cropper.js instance.
         */
        initializeCropper() {
            this.destroyCropper()

            const cropImage = this.$refs.cropModal?.getCropImageElement()

            if (!cropImage) {
                return
            }

            this.cropperInstance = new Cropper(cropImage, {
                aspectRatio: this.field.aspectRatio,
                viewMode: 1,
                dragMode: 'move',
                autoCropArea: 1,
                responsive: true,
            })
        },

        /**
         * Destroys the active Cropper.js instance.
         */
        destroyCropper() {
            this.cropperInstance?.destroy()
            this.cropperInstance = null
        },

        /**
         * Closes the crop dialog and resets its state.
         */
        closeCropDialog() {
            this.isCropDialogOpen = false
            this.cropSource = null
            this.editingImageId = null
        },

        /**
         * Builds a cropped canvas with product image size limits.
         *
         * @returns {HTMLCanvasElement|null}
         */
        getCroppedCanvas() {
            if (!this.cropperInstance) {
                return null
            }

            return this.cropperInstance.getCroppedCanvas({
                maxWidth: this.field.maxWidth,
                maxHeight: this.field.maxHeight,
                imageSmoothingQuality: 'high',
            })
        },

        /**
         * Converts a canvas to a compressed JPEG blob.
         *
         * @param {HTMLCanvasElement} canvas
         * @returns {Promise<Blob>}
         */
        canvasToBlob(canvas) {
            return new Promise((resolve, reject) => {
                canvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Unable to process image')),
                    'image/jpeg',
                    this.field.jpegQuality,
                )
            })
        },

        /**
         * Stores the cropped image locally until the Nova form is submitted.
         */
        async confirmCrop() {
            const canvas = this.getCroppedCanvas()

            if (!canvas) {
                return
            }

            this.cropError = null
            this.isProcessing = true

            const imageId = this.editingImageId || this.createImageId()

            try {
                const blob = await this.canvasToBlob(canvas)
                const context = canvas.getContext('2d', { willReadFrequently: true })
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
                const blurhash = encode(imageData.data, canvas.width, canvas.height, 4, 3)
                const previewUrl = URL.createObjectURL(blob)
                const pendingFile = new File([blob], 'image.jpg', { type: 'image/jpeg' })

                const nextImage = {
                    id: imageId,
                    previewUrl,
                    storedUrl: null,
                    pendingFile,
                    blurhash,
                }

                if (this.editingImageId) {
                    const index = this.images.findIndex(image => image.id === this.editingImageId)

                    if (index !== -1) {
                        revokeObjectUrl(this.images[index].previewUrl)
                        this.images.splice(index, 1, nextImage)
                    }
                } else {
                    this.images.push(nextImage)

                    if (this.images.length === 1) {
                        this.coverIndex = 0
                    }
                }

                this.markDirty()
                this.closeCropDialog()
            } catch (error) {
                this.cropError = error?.message || this.__('Unable to crop image')
            } finally {
                this.isProcessing = false
            }
        },

        /**
         * Sets the cover image index.
         *
         * @param {number} index
         */
        setCover(index) {
            this.coverIndex = index
            this.markDirty()
        },

        /**
         * Removes an image from the collection.
         *
         * @param {number} index
         */
        removeImage(index) {
            revokeObjectUrl(this.images[index]?.previewUrl)
            this.images.splice(index, 1)

            if (this.coverIndex >= this.images.length) {
                this.coverIndex = Math.max(0, this.images.length - 1)
            }

            this.markDirty()
        },

        /**
         * Orders images with the cover image first.
         *
         * @returns {Array}
         */
        orderedImages() {
            if (!this.images.length) {
                return []
            }

            const cover = this.images[this.coverIndex] || this.images[0]
            const rest = this.images.filter((_, index) => index !== this.coverIndex)

            return [cover, ...rest]
        },

        /**
         * Builds the manifest and pending files for Nova form submission.
         *
         * @returns {{manifest: Array, pendingFiles: Array<File>}}
         */
        buildSubmitPayload() {
            const manifest = []
            const pendingFiles = []
            let pendingIndex = 0

            this.orderedImages().forEach((image) => {
                if (image.pendingFile) {
                    manifest.push({
                        type: 'pending',
                        index: pendingIndex,
                        blurhash: image.blurhash,
                    })
                    pendingFiles.push(image.pendingFile)
                    pendingIndex++
                } else if (image.storedUrl) {
                    manifest.push({
                        type: 'existing',
                        url: image.storedUrl,
                        blurhash: image.blurhash,
                    })
                }
            })

            return { manifest, pendingFiles }
        },

        /**
         * Fill the given FormData object with the field's internal value.
         *
         * @param {FormData} formData
         */
        fill(formData) {
            const { manifest, pendingFiles } = this.buildSubmitPayload()

            formData.append(this.field.attribute, JSON.stringify(manifest))

            pendingFiles.forEach((file, index) => {
                formData.append(`${this.field.attribute}_pending[${index}]`, file, `image-${index}.jpg`)
            })
        },

        /**
         * Mark the parent Nova form as dirty.
         */
        markDirty() {
            this.$emit('field-changed')
        },
    },
}
</script>
